/**
 * All main application json data calls are made using this class.
 *
 * @Author: Jon Lucas
 * @Date:   2017-09-11T17:49:51+02:00
 * @Email:  jon.lucas@mazedia.fr
 * @Last modified by:   Jon Lucas
 * @Last modified time: 2017-09-29T16:08:36+02:00
 */

import { Injectable } from "@angular/core";
import { Headers, Http } from "@angular/http";

import { environment } from "../environments/environment";

import "rxjs/add/operator/toPromise";

import { BaseDataType } from "./base-data-type";
import { LabelType } from "./label-type";

@Injectable()
export class AppService {

    // Application's data urls
    
    public static readonly USER_INTERFACE_LABELS_URL: string = "assets/config/" + environment.grandCompte + "/userInterfaceLabels.json";
    public static readonly NAVIGATION_MENU_URL: string = "assets/config/" + environment.grandCompte + "/navigationMenu.json";
    public static readonly ROOF_MATERIAL_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/roofMaterialTypes.json";
    public static readonly WALLS_MATERIAL_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/wallsMaterialTypes.json";
    public static readonly PROJECT_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/projectTypes.json";
    public static readonly PROFILE_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/profileTypes.json";
    public static readonly DESCENTE_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/descenteTypes.json";
    public static readonly BELRIV_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/belrivTypes.json";
    public static readonly ROOF_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/roofTypes.json";
    public static readonly SURELEVATION_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/surelevationTypes.json";
    public static readonly ROOF_DIMENSIONS_URL: string = "assets/config/" + environment.grandCompte + "/roofDimensions.json";
    public static readonly SURELEVATION_DIMENSIONS_URL: string = "assets/config/" + environment.grandCompte + "/surelevationDimensions.json";
    public static readonly INSTALLATION_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/installationTypes.json";
    public static readonly CROCHET_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/crochetTypes.json";
    public static readonly WALL_PROFILE_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/wallProfileTypes.json";
    public static readonly BELRIV_DIMENSIONS_URL: string = "assets/config/" + environment.grandCompte + "/belrivDimensions.json";
    public static readonly ACCESSORIES_TYPES_URL: string = "assets/config/" + environment.grandCompte + "/accessories.json";
    public static readonly CONTACT_FORM_URL: string = "assets/config/" + environment.grandCompte + "/contactForm.json";
    public static readonly CAMERA_DEFAULTS_URL: string = "assets/config/" + environment.grandCompte + "/cameraDefaults.json";
    public static readonly THREED_VIEW_LANDMARKS_URL: string = "assets/config/" + environment.grandCompte + "/threedViewLandmarks.json";

    public readonly TAG: string = "[AppService]";

    public responses: { [key: string]: BaseDataType[] } = {};

    public inputFieldMessages: { [key: string]: LabelType };

    constructor(private http: Http) { }

    /**
     * Retrieve application's json data.
     *
     * @param pUrl string
     */
    public getData(pUrl: string): Promise<BaseDataType[]> {
        console.log(this.TAG, "getData()", pUrl);

        if (pUrl === undefined) {
            return Promise.reject("AppService's getData() method's pUrl property is undefined.");
        }

        // Return cached response
        if (this.responses[pUrl]) {
            // return new Promise((resolve, reject) => resolve(this.responses[pUrl]) );
            return Promise.resolve(this.responses[pUrl]);
        }
        // Else make HTTP request
        return this.http.get(pUrl)
            .toPromise()
            .then(response => {
                console.log(this.TAG, "getData()", pUrl, "response =>", response.json());
                this.responses[pUrl] = response.json() as BaseDataType[];
                return this.responses[pUrl];
            })
            .catch(this.handleError);
    }

    /**
     * Get element's basedatatype from application's json data, by its id.
     *
     * @param pUrl string
     * @param pId string
     */
    public getType(pUrl: string, pId: string): Promise<BaseDataType> {
        console.log(this.TAG, "getType()", pUrl, pId);
        if (pUrl === undefined) {
            return Promise.reject("AppService's getType() method's pUrl property is undefined.");
        }

        return this.getData(pUrl)
            .then(
                response => {
                    // console.log("RESPONSE", response);
                    return this.responses[pUrl].find(type => type.id === pId);
                }
            )
            .catch(this.handleError);
    }

    /**
     * Get UI label type filled in 'userInterfaceLabels.json' file.
     *
     * @param pId label id.
     */
    public getLabel(pId: string): Promise<LabelType>
    {
        return this.getType(AppService.USER_INTERFACE_LABELS_URL, pId)
        .then(type => (type && type.label) ? type.label : <LabelType>{"fr": "&empty;", "en": "&empty;", "es": "&empty;"})
        .catch(this.handleError);
    }

    /**
     * Get input fields' labels dictionnary, filled in 'userInterfaceLabels.json' file.
     */
    public getInputFieldMessagesTypes(): Promise<{[key: string]: LabelType}>
    {
        // Return cached response
        if (this.inputFieldMessages) {
            // return new Promise((resolve, reject) => resolve(this.responses[pUrl]) );
            return Promise.resolve(this.inputFieldMessages);
        }

        return this.getType(AppService.USER_INTERFACE_LABELS_URL, "input-field")
            .then(type => {
                if (this.inputFieldMessages === undefined)
                {
                    this.inputFieldMessages = {};
                    for (let i = 0; i < type.values.length; i++)
                    {
                        this.inputFieldMessages[type.values[i].id] = type.values[i].label;
                        // console.log(this.inputFieldMessages["default-message"]);
                    }
                    return this.inputFieldMessages;
                }
            })
            .catch(this.handleError);
    }

    /**
     * Get input field message's label type.
     *
     * @param pId message id
     */
    public getInputFieldMessage(pId: string): Promise<LabelType>
    {
        if (this.inputFieldMessages === undefined)
        {
            return this.getInputFieldMessagesTypes()
                .then(type => this.getInputFieldMessage(pId))
                .catch(this.handleError);
        }
        return Promise.resolve(this.inputFieldMessages[pId]);
    }


    private handleError(error: any): Promise<any> {
        console.error("An error occurred", error); // for demo purposes only
        return Promise.reject(error.message || error);
    }

}



// WEBPACK FOOTER //
// ./src/app/app.service.ts